/**
 * Iconurile pe care le poate purta o firmă a grupului.
 *
 * **Listă închisă, nu string liber.** Coloana `crm_companies.icon` ajunge într-un
 * `Icons[valoare]` în frontend: cu o valoare liberă, un nume greșit ar randa `undefined`
 * într-un `<Component />` — adică un ecran alb, nu un icon lipsă. Aici valoarea e validată
 * la scriere, deci ce e în bază se poate reda întotdeauna.
 *
 * Stă în `shared`, ca și registrul de permisiuni, **exact din același motiv**: lista
 * trebuie să fie aceeași în cele două capete. Backendul validează după ea, frontendul
 * mapează după ea la componente `lucide-react`. Două copii ar diverge, iar divergența
 * s-ar vedea abia ca icon dispărut la o firmă anume.
 *
 * Numele sunt cele din `lucide-react`, neschimbate — o redenumire aici ar cere migrare de
 * date pentru nimic. Selecția e cea utilă unui grup de firme de internet; se extinde prin
 * adăugarea unei linii, iar valorile vechi rămân valide.
 */
export const companyIconKeys = [
  'Building2',
  'Antenna',
  'Wifi',
  'Router',
  'Network',
  'Server',
  'SatelliteDish',
  'Radio',
  'Cable',
  'Signal',
  'Globe',
  'Phone',
  'Tv',
  'Zap',
  'Store',
  'Factory',
] as const;

export type CompanyIconKey = (typeof companyIconKeys)[number];

/**
 * Ce se folosește când firma n-are icon ales.
 *
 * Coloana e `NULL`-abilă (firmele existente n-au ales nimic), dar interfața nu are stare
 * de „fără icon": ar fi un gol în listă exact acolo unde ochiul caută reperul.
 */
export const DEFAULT_COMPANY_ICON: CompanyIconKey = 'Building2';

/** Lungimea coloanei `crm_companies.icon`. Cel mai lung nume de azi are 13 caractere. */
export const COMPANY_ICON_MAX_LENGTH = 40;

const companyIconKeySet: ReadonlySet<string> = new Set<string>(companyIconKeys);

export function isCompanyIconKey(value: string): value is CompanyIconKey {
  return companyIconKeySet.has(value);
}

/**
 * Valoarea din bază → un icon care sigur se poate reda.
 *
 * Se **ignoră** ce nu e cunoscut, nu se aruncă: un nume scos din listă la un refactor
 * rămâne pe rândurile vechi, iar o eroare acolo ar face ca o firmă întreagă să nu se mai
 * poată deschide din cauza unui icon. Aceeași alegere ca la `filterKnownPermissionKeys`.
 */
export function normalizeCompanyIcon(value: string | null | undefined): CompanyIconKey {
  return value && isCompanyIconKey(value) ? value : DEFAULT_COMPANY_ICON;
}
